import { useState, Fragment } from "react";
import EditAccountModal from "./EditAccountModal";
import AddAccountModal from "./AddAccountModal";
import BankLimitModal from "./BankLimitModal";
import axios from "axios";
import { getApiUrl } from "../../config/api";

function AccountDetails({
  accounts,
  companyId,
  onRefresh,
  onDeleteAccount,
  onAddAccount,
}) {
  const [editAccount, setEditAccount] = useState(null); // State for editing an account
  const [isAddAccountModalOpen, setIsAddAccountModalOpen] = useState(false); // State for "Add Account" modal
  const [expandedRowId, setExpandedRowId] = useState(null); // Track the ID of the currently expanded row

  const [activeBankLimitModal, setActiveBankLimitModal] = useState({ isOpen: false, account: null, bankLimit: null });

  // Toggle expanded row
  const toggleRow = (id) => {
    if (expandedRowId === id) {
      setExpandedRowId(null); // Collapse the row if it's already expanded
    } else {
      setExpandedRowId(id); // Expand the clicked row
    }
  };

  const handleDeleteBankLimit = async (limitId) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this bank limit?");
    if (!confirmDelete) return;

    try {
      const accessToken = localStorage.getItem("access_token");
      await axios.delete(getApiUrl(`/PartnerFiles/api/bank-limits/${limitId}/`), {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      alert("Bank limit deleted successfully.");
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error("Error deleting bank limit:", error);
      alert("Failed to delete bank limit.");
    }
  };

  return (
    <div className="mt-5 mb-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-2xl font-semibold text-gray-800">Account Details</h3>
        {/* Add Account Button */}
        <button
          onClick={() => setIsAddAccountModalOpen(true)}
          className="bg-blue-500 hover:bg-blue-600 py-2 px-4 rounded text-white transition-colors duration-300"
        >
          Add Account
        </button>
      </div>
      <div className="w-full overflow-x-auto">
        <table className="w-full border-collapse mt-3">
          <thead>
            <tr>
              <th className="bg-blue-500 text-white p-3 text-left border-b border-blue-600"></th> {/* Expand/Collapse Icon */}
              <th className="bg-blue-500 text-white p-3 text-left border-b border-blue-600">Account Name</th>
              <th className="bg-blue-500 text-white p-3 text-left border-b border-blue-600">Account Number</th>
              <th className="bg-blue-500 text-white p-3 text-left border-b border-blue-600">Company</th>
              <th className="bg-blue-500 text-white p-3 text-left border-b border-blue-600">IFSC Code</th>
              <th className="bg-blue-500 text-white p-3 text-left border-b border-blue-600">Branch</th>
              <th className="bg-blue-500 text-white p-3 text-left border-b border-blue-600">Bank Name</th>
              <th className="bg-blue-500 text-white p-3 text-left border-b border-blue-600">Signing Authority</th>
              <th className="bg-blue-500 text-white p-3 text-left border-b border-blue-600">Account Limit</th>
              <th className="bg-blue-500 text-white p-3 text-left border-b border-blue-600">Account Type</th>
              <th className="bg-blue-500 text-white p-3 text-left border-b border-blue-600">Contact Number</th>
              <th className="bg-blue-500 text-white p-3 text-left border-b border-blue-600">Actions</th>
            </tr>
          </thead>
          <tbody>
            {accounts.map((account) => (
              <Fragment key={account.id}>
                {/* Main Row */}
                <tr
                  className="border-b border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors duration-150"
                  onClick={() => toggleRow(account.id)} // Expand/Collapse row
                >
                  <td className="p-3">
                    {expandedRowId === account.id ? "-" : "+"}
                  </td>
                  <td className="p-3">{account.acc_name}</td>
                  <td className="p-3">{account.acc_number}</td>
                  <td className="p-3">{account.company}</td>
                  <td className="p-3">{account.ifsc_code}</td>
                  <td className="p-3">{account.branch}</td>
                  <td className="p-3">{account.bank_name}</td>
                  <td className="p-3">{account.signing_authority}</td>
                  <td className="p-3">{account.acc_limit}</td>
                  <td className="p-3">{account.acc_type}</td>
                  <td className="p-3">{account.contact_no}</td>
                  <td className="p-3">
                    <div className="flex gap-2">
                      {/* Edit Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent row toggle
                          setEditAccount(account);
                        }}
                        className="bg-green-500 hover:bg-green-600 py-1 px-3 rounded text-white text-sm transition-colors duration-300"
                      >
                        Edit
                      </button>
                      {/* Delete Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent row toggle
                          onDeleteAccount(account);
                        }}
                        className="bg-red-500 hover:bg-red-600 py-1 px-3 rounded text-white text-sm transition-colors duration-300"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>

                {/* Expanded Row */}
                {expandedRowId === account.id && (
                  <tr>
                    <td colSpan="11" className="p-4 bg-gray-50">
                      <div className="ml-5">
                        <h4 className="font-bold text-gray-700 mb-2 flex items-center gap-2">
                          Bank Limits:
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveBankLimitModal({ isOpen: true, account: account, bankLimit: null });
                            }}
                            className="bg-blue-500 hover:bg-blue-600 text-white text-xs py-1 px-2 rounded"
                          >
                            + Add Limit
                          </button>
                        </h4>
                        <ul className="list-disc pl-5 mb-5">
                          {account.bank_limits && account.bank_limits.length > 0 ? (
                            account.bank_limits.map((bl) => (
                              <li key={bl.id} className="mb-2 text-gray-600 flex items-center gap-3">
                                <span>{`Year: ${bl.year}, Limit: ₹${bl.limit}`}</span>
                                <div className="flex gap-2">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setActiveBankLimitModal({ isOpen: true, account: account, bankLimit: bl });
                                    }}
                                    className="text-green-600 hover:text-green-800 text-xs font-semibold"
                                  >
                                    Edit
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteBankLimit(bl.id);
                                    }}
                                    className="text-red-600 hover:text-red-800 text-xs font-semibold"
                                  >
                                    Delete
                                  </button>
                                </div>
                              </li>
                            ))
                          ) : (
                            <li className="mb-2 text-gray-500 italic">No year-wise bank limits found.</li>
                          )}
                        </ul>
                        <h4 className="font-bold text-gray-700 mb-2">ATMs:</h4>
                        <ul className="list-disc pl-5 mb-3">
                          {account.atms?.map((atm) => (
                            <li key={atm.id} className="mb-1 text-gray-600">
                              {`ATM Number: ${atm.atm_number}, Expiry: ${atm.expiry}`}
                            </li>
                          ))}
                        </ul>
                        <h4 className="font-bold text-gray-700 mb-2">Cheques:</h4>
                        <ul className="list-disc pl-5">
                          {account.cheques?.map((cheque) => (
                            <li key={cheque.id} className="mb-1 text-gray-600">
                              {`Cheque Range: ${cheque.cheque_number_start} - ${cheque.cheque_number_end}`}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal for editing an account */}
      {editAccount && (
        <EditAccountModal
          account={editAccount}
          onClose={() => setEditAccount(null)}
        />
      )}

      {/* Modal for adding a new account */}
      {isAddAccountModalOpen && (
        <AddAccountModal
          onClose={() => setIsAddAccountModalOpen(false)}
          companyId={companyId}
          onAddAccount={(newAccount) => {
            onAddAccount(newAccount); // Call the parent function to add the account
            setIsAddAccountModalOpen(false); // Close the modal
          }}
        />
      )}

      {/* Modal for Bank Limit */}
      {activeBankLimitModal.isOpen && (
        <BankLimitModal
          account={activeBankLimitModal.account}
          bankLimit={activeBankLimitModal.bankLimit}
          onClose={() => setActiveBankLimitModal({ isOpen: false, account: null, bankLimit: null })}
          onRefresh={onRefresh}
        />
      )}
    </div>
  );
}

export default AccountDetails;